import React, { useState, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import { getStaffQuarterPriorityList, getStaffQuarterLocations, getStaffQuarterQuarterTypes, generateStaffQuarterWillingness, exportStaffQuarterPriorityList, getStaffQuarterPools } from "../../../../core/data/redux/staffQuarterSlice";
import { toast } from "react-toastify";
import { Modal, Select } from "antd";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import Table from "../../../../core/common/dataTable/index";
import { encodeRouteParam } from "../../../../core/hooks/routeEncoder";

/* ================= TYPES ================= */
interface PriorityRecord {
    key: string;
    priorityNo: number;
    empNo: string;
    name: string;
    designation: string;
    department: string;
    regDate: string;
    category: string;
    status: string;
    quarterType: string;
    location: string | string[];
    locationCode?: string;
    willingnessReferenceNumber?: string;
    willingnessStatus?: string;
    isWillingnessGenerated?: boolean;
    locationPreference1: string;
    locationPreference2: string;
    locationPreference3: string;
    registrationReferenceNumber: string;
    pool: string;
    dateOfBirth: string;
    dateOfJoining: string;
    begda: string;
    endDate: string;
}

interface TableColumn<T> {
    title: string;
    key: string;
    className?: string;
    render?: (value: any, record: T, index: number) => React.ReactNode;
    sorter?: (a: T, b: T) => number;
}

/* ================= COMPONENTS ================= */
const DashboardTile = ({
    title,
    icon,
    count,
    link,
    colorClass = "text-primary",
}: {
    title: string;
    icon?: string;
    count?: string | number;
    link: string;
    colorClass?: string;
}) => {
    return (
        <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-6 col-sm-6 mb-3">
            <div
                className="card h-100 border-0 shadow-sm hover-card"
                style={{
                    borderRadius: "16px",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    overflow: "hidden",
                    backgroundColor: "#fff"
                }}
            >
                <div className="card-body d-flex align-items-center p-3">
                    <div
                        className={`d-flex align-items-center justify-content-center me-3 rounded-circle`}
                        style={{
                            width: "48px",
                            height: "48px",
                            backgroundColor: "rgba(var(--bs-primary-rgb), 0.03)",
                            border: "1px solid rgba(0,0,0,0.03)"
                        }}
                    >
                        {icon && <i className={`ti ${icon} ${colorClass}`} style={{ fontSize: "24px" }}></i>}
                    </div>
                    <div>
                        <div className="text-muted small mb-1" style={{ fontSize: "12px", letterSpacing: "0.2px" }}>{title}</div>
                        <div className={`h4 fw-bold mb-0 ${colorClass}`}>{count}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const DashboardSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
    return (
        <div className="mb-4">
            <h5 className="fw-bold text-secondary mb-3 ms-1" style={{ fontSize: "15px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {title}
            </h5>
            <div className="row">{children}</div>
        </div>
    );
};

const PriorityList = () => {
    const routes = all_routes;

    const dispatch = useDispatch<any>();
    const [apiData, setApiData] = useState<any[]>([]);
    const [overview, setOverview] = useState({
        totalApplications: 0,
        activePriority: 0,
        deListed: 0,
        type2Requests: 0
    });

    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    const [searchTerm, setSearchTerm] = useState("");
    const [selectedType, setSelectedType] = useState("");
    const [selectedLocation, setSelectedLocation] = useState("");
    const [selectedPoolId, setSelectedPoolId] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");

    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<any>(null);
    const [isGenerating, setIsGenerating] = useState(false);

    const handleExport = () => {
        setIsExporting(true);
        dispatch(exportStaffQuarterPriorityList({})).then((res: any) => {
            if (res.payload) {
                // Determine MIME type (default to CSV as seen in backend response)
                const blob = res.payload instanceof Blob
                    ? res.payload
                    : new Blob([res.payload], { type: 'text/csv' });

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `PriorityList_${new Date().getTime()}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                toast.success("Priority list exported successfully!");
            } else {
                toast.error("Failed to export priority list.");
            }
            setIsExporting(false);
        }).catch(() => {
            toast.error("An error occurred during export.");
            setIsExporting(false);
        });
    };

    const fetchData = (page = pagination.current, limit = pagination.pageSize, type = selectedType, search = searchTerm, location = selectedLocation, pool = selectedPoolId, category = selectedCategory) => {
        setIsLoading(true);
        const qCode = type && type !== "All Types" ? (isNaN(Number(type)) ? type : Number(type)) : undefined;
        const qTypeObj = dynamicQuarterTypes.find((t: any) => String(t.code) === String(type) || String(t.id) === String(type));
        const qTypeName = qTypeObj ? (qTypeObj.name || qTypeObj.quarterType) : (typeof type === "string" && type.includes("Type") ? type : (qCode ? `Type-${qCode}` : undefined));

        const payload = {
            page: page,
            limit: limit,
            ...(qCode !== undefined && { quarterTypeCode: qCode, quarter_type_code: qCode }),
            ...(qTypeName && { quarterType: qTypeName }),
            ...(category && category !== "All Categories" && { category: category }),
            ...(search && search.trim() !== "" ? {
                search: search.trim(),
                empId: search.trim(),
                employeeNumber: search.trim(),
                empName: search.trim(),
                employeeName: search.trim()
            } : {}),
            ...(location && location !== "All Locations" && { locationId: isNaN(Number(location)) ? location : Number(location) }),
            ...(pool && pool !== "All Pools" && { poolTypeId: isNaN(Number(pool)) ? pool : Number(pool) }),
            status: "APPROVED"
        };

        dispatch(getStaffQuarterPriorityList(payload)).then((res: any) => {
            if (res.payload?.success && res.payload.data) {
                const resultData = res.payload.data;
                let list = resultData.list || resultData.data || [];
                // Filter out records where willingness is already approved
                list = list.filter((item: any) => item.isWillingnessApproved !== true && item.isWillingnessApproved !== "true" && item.isWillingnessApproved !== 1);

                // Backup client-side filter for pool if API doesn't support it directly
                if (pool && pool !== "All Pools") {
                    list = list.filter((item: any) => {
                        const itemPoolId = item.poolTypeId || item.pool_type_id || item.poolId || item.pool_id || item.pool?.id;
                        return String(itemPoolId) === String(pool);
                    });
                }
                setApiData(list);

                // The screenshot shows pagination is inside a 'pagination' object
                // NOTE: It looks like there might be a typo in the backend: "lolalCount"
                const total = resultData.pagination?.totalCount ||
                    resultData.pagination?.lolalCount ||
                    resultData.total ||
                    resultData.totalCount ||
                    0;

                // Trust the requested page/limit for UI state to avoid jumping back to page 1
                // if the server echoes default values incorrectly.
                setPagination(prev => ({
                    ...prev,
                    current: page,
                    pageSize: limit,
                    total: total
                }));
                if (resultData.overview) {
                    setOverview(resultData.overview);
                }
            }
            setIsLoading(false);
        });
    };

    useEffect(() => {
        fetchData();
    }, [dispatch]);

    const handlePagination = (newPagination: any) => {
        fetchData(newPagination.current, newPagination.pageSize, selectedType, searchTerm, selectedLocation, selectedPoolId, selectedCategory);
    };

    const handleSearch = (query: string) => {
        setSearchTerm(query);
        fetchData(1, pagination.pageSize, selectedType, query, selectedLocation, selectedPoolId, selectedCategory);
    };

    const handleConfirmWillingness = () => {
        if (!selectedRecord) return;
        setIsGenerating(true);
        dispatch(generateStaffQuarterWillingness({
            registrationId: Number(selectedRecord.key),
            locationCode: selectedRecord.locationCode
        })).then((res: any) => {
            if (res.payload?.success || res.payload?.status === 200) {
                toast.success(res.payload.message || "Willingness generated successfully");
                setIsConfirmModalOpen(false);
                fetchData(); // Refresh list to reflect changes
            } else {
                const errorMsg = typeof res.payload === 'string' ? res.payload : (res.payload?.message || "Failed to generate willingness");
                toast.error(errorMsg);
            }
            setIsGenerating(false);
        }).catch(() => {
            setIsGenerating(false);
            toast.error("An error occurred");
        });
    };

    const formatDateOnly = (val: any) => {
        if (!val || val === "-" || val === "N/A") return "-";
        if (typeof val === 'string' && /^\d{2}-\d{2}-\d{2,4}$/.test(val)) return val;
        const d = new Date(val);
        if (isNaN(d.getTime())) return val;
        const day = String(d.getDate()).padStart(2, "0");
        const month = d.toLocaleString('default', { month: 'short' });
        const year = d.getFullYear();
        return `${day} ${month}, ${year}`;
    };

    const filteredApiData = useMemo(() => {
        if (!Array.isArray(apiData) || apiData.length === 0) return [];

        let list = apiData;

        if (searchTerm && searchTerm.trim() !== "") {
            const term = searchTerm.trim().toLowerCase();
            list = list.filter((item: any) => {
                const empNo = String(item.employeeNumber || item.employeeId || item.empId || item.empNo || "").toLowerCase();
                const empName = String(item.employeeName || item.name || item.empName || "").toLowerCase();
                const designation = String(item.designation || "").toLowerCase();
                const refNo = String(item.referenceNumber || item.registrationNo || item.registrationNumber || "").toLowerCase();
                return empNo.includes(term) || empName.includes(term) || designation.includes(term) || refNo.includes(term);
            });
        }

        return list;
    }, [apiData, searchTerm]);

    const data: PriorityRecord[] = filteredApiData.map((item: any, idx: number) => {
        let prefs = [item.locationPreference1, item.locationPreference2, item.locationPreference3].filter(p => p && p !== "N/A" && p !== "-");
        if (prefs.length === 0 && item.location && item.location !== "N/A" && item.location !== "-") {
            prefs = [item.location];
        }

        // Resolve willingness details based on selected location preference
        let isWillingnessGenerated = false;
        let willingnessReferenceNumber = null;
        let willingnessStatus = null;

        const activeLocId = selectedLocation && selectedLocation !== "All Locations" ? Number(selectedLocation) : null;
        if (activeLocId) {
            if (item.locationPreference1Id === activeLocId) {
                isWillingnessGenerated = !!(item.location1IsWillingnessGenerated || item.isWillingnessGeneratedForLoc1);
                willingnessReferenceNumber = item.location1WillingnessReferenceNumber;
                willingnessStatus = item.location1WillingnessStatus;
            } else if (item.locationPreference2Id === activeLocId) {
                isWillingnessGenerated = !!(item.location2IsWillingnessGenerated || item.isWillingnessGeneratedForLoc2);
                willingnessReferenceNumber = item.location2WillingnessReferenceNumber;
                willingnessStatus = item.location2WillingnessStatus;
            } else if (item.locationPreference3Id === activeLocId) {
                isWillingnessGenerated = !!(item.location3IsWillingnessGenerated || item.isWillingnessGeneratedForLoc3);
                willingnessReferenceNumber = item.location3WillingnessReferenceNumber;
                willingnessStatus = item.location3WillingnessStatus;
            }
        } else {
            isWillingnessGenerated = !!(
                item.location1IsWillingnessGenerated || item.isWillingnessGeneratedForLoc1 ||
                item.location2IsWillingnessGenerated || item.isWillingnessGeneratedForLoc2 ||
                item.location3IsWillingnessGenerated || item.isWillingnessGeneratedForLoc3 ||
                item.isWillingnessGenerated
            );
            willingnessReferenceNumber = item.location1WillingnessReferenceNumber || 
                                         item.location2WillingnessReferenceNumber || 
                                         item.location3WillingnessReferenceNumber || 
                                         item.willingnessReferenceNumber;
            willingnessStatus = item.location1WillingnessStatus || 
                                item.location2WillingnessStatus || 
                                item.location3WillingnessStatus || 
                                item.willingnessStatus;
        }

        const dob = item.dateOfBirth || item.dob || item.date_of_birth || null;
        const doj = item.dateOfJoining || item.doj || item.date_of_joining || null;
        const getRetirementDate = (dobStr: any) => {
            if (!dobStr || dobStr === "-" || dobStr === "N/A") return null;
            const d = new Date(dobStr);
            if (isNaN(d.getTime())) return null;
            const year = d.getFullYear() + 60;
            const month = d.getMonth();
            const lastDay = new Date(year, month + 1, 0);
            return lastDay.toISOString();
        };

        return {
            key: item.id?.toString() || idx.toString(),
            priorityNo: parseInt(item.priority?.replace("#", "") || "0", 10),
            empNo: item.employeeNumber || item.empId || item.employeeId || "",
            name: item.employeeName || item.name || item.empName || "",
            designation: item.designation || "",
            department: item.department || item.dept || "Operations", 
            regDate: item.registrationTimestamp || item.registrationDate || item.createdAt || "",
            category: item.category || "",
            status: item.willingnessStatus || item.status || "",
            quarterType: (item.quarterType || item.quarter_type || "").split(" ")[0], // Keep only "Type-X" part
            location: prefs,
            locationPreference1: item.locationPreference1 || "-",
            locationPreference2: item.locationPreference2 || "-",
            locationPreference3: item.locationPreference3 || "-",
            locationCode: item.locationCode || item.location || item.locationPreference1 || null,
            willingnessReferenceNumber: willingnessReferenceNumber || null,
            willingnessStatus: willingnessStatus || null,
            isWillingnessGenerated: isWillingnessGenerated,
            registrationReferenceNumber: item.referenceNumber || item.registrationNo || item.registrationNumber || "N/A",
            pool: item.poolType || item.pool_type || item.pool?.name || item.poolName || item.pool_name || item.pool?.namePool || item.pool || "-",
            dateOfBirth: dob || "-",
            dateOfJoining: doj || "-",
            begda: item.begda || item.begDate || item.beginDate || item.startDate || item.payDate || doj || "-",
            endDate: item.endDate || item.end_date || item.expiryDate || (dob ? getRetirementDate(dob) : null) || "-"
        };
    });

    /* ================= STATE & FILTERS ================= */
    const [dynamicLocations, setDynamicLocations] = useState<any[]>([]);
    const [dynamicQuarterTypes, setDynamicQuarterTypes] = useState<any[]>([]);
    const [dynamicPools, setDynamicPools] = useState<any[]>([]);

    useEffect(() => {
        dispatch(getStaffQuarterLocations({})).then((res: any) => {
            if (res.payload?.success) setDynamicLocations(res.payload.data || []);
        });
        dispatch(getStaffQuarterQuarterTypes({})).then((res: any) => {
            if (res.payload?.success) setDynamicQuarterTypes(res.payload.data || []);
        });
        dispatch(getStaffQuarterPools({})).then((res: any) => {
            if (res.payload?.success) setDynamicPools(res.payload.data || []);
        });
    }, [dispatch]);

    // Dynamic Options
    const filterOptions = useMemo(() => {
        return {
            types: [{ id: "", code: "", name: "All Types" }, ...dynamicQuarterTypes],
            locations: [{ id: "", name: "All Locations" }, ...dynamicLocations],
            pools: [{ id: "", namePool: "All Pools" }, ...dynamicPools]
        };
    }, [dynamicQuarterTypes, dynamicLocations, dynamicPools]);

    const stats = useMemo(() => ({
        total: overview.totalApplications,
        active: overview.activePriority,
        delisted: overview.deListed,
        typeII: overview.type2Requests,
    }), [overview]);

    const columns = [
        {
            title: "Priority #",
            dataIndex: "priorityNo",
            key: "priorityNo",
            className: "text-center",
            render: (val: any) => <span className="fw-bold text-primary">#{val}</span>,
            sorter: (a: any, b: any) => a.priorityNo - b.priorityNo
        },
        {
            title: "Employee",
            dataIndex: "name",
            key: "name",
            render: (val: any, record: PriorityRecord) => (
                <div>
                    <div className="fw-bold text-dark">{val}</div>
                    <div className="text-muted small">{record.empNo} | {record.designation}</div>
                </div>
            ),
            sorter: (a: any, b: any) => a.name.localeCompare(b.name)
        },
        {
            title: "DOB",
            dataIndex: "dateOfBirth",
            key: "dateOfBirth",
            render: (val: any) => <span className="text-dark" style={{ fontSize: '13px' }}>{formatDateOnly(val)}</span>
        },
        {
            title: "DOJ",
            dataIndex: "dateOfJoining",
            key: "dateOfJoining",
            render: (val: any) => <span className="text-dark" style={{ fontSize: '13px' }}>{formatDateOnly(val)}</span>
        },
        {
            title: "Quarter Type",
            dataIndex: "quarterType",
            key: "quarterType",
            render: (val: any) => <span className="badge bg-light text-dark border">{val}</span>
        },
        {
            title: "Pool",
            dataIndex: "pool",
            key: "pool",
            render: (val: any) => <span className="badge bg-outline-secondary border text-secondary" style={{ fontSize: '12px' }}>{val}</span>
        },
        {
            title: "Location 1",
            dataIndex: "locationPreference1",
            key: "locationPreference1",
            render: (val: any) => <span className="fw-medium text-dark" style={{ fontSize: '12.5px' }}>{val || "-"}</span>
        },
        {
            title: "Location 2",
            dataIndex: "locationPreference2",
            key: "locationPreference2",
            render: (val: any) => <span className="fw-medium text-dark" style={{ fontSize: '12.5px' }}>{val || "-"}</span>
        },
        {
            title: "Location 3",
            dataIndex: "locationPreference3",
            key: "locationPreference3",
            render: (val: any) => <span className="fw-medium text-dark" style={{ fontSize: '12.5px' }}>{val || "-"}</span>
        },
        {
            title: "Registration Date",
            dataIndex: "regDate",
            key: "regDate",
            render: (val: any) => {
                const d = new Date(val);
                const day = String(d.getDate()).padStart(2, "0");
                const month = d.toLocaleString('default', { month: 'short' });
                const year = d.getFullYear();
                const hours = String(d.getHours()).padStart(2, "0");
                const minutes = String(d.getMinutes()).padStart(2, "0");

                return (
                    <div className="d-flex flex-column">
                        <span className="text-dark fw-medium" style={{ fontSize: '13px' }}>
                            <i className="ti ti-calendar-event me-1 text-muted"></i>
                            {`${day} ${month}, ${year}`}
                        </span>
                        <span className="text-muted extra-small" style={{ fontSize: '11px' }}>
                            <i className="ti ti-clock me-1"></i>
                            {`${hours}:${minutes}`}
                        </span>
                    </div>
                );
            },
            sorter: (a: any, b: any) => new Date(a.regDate).getTime() - new Date(b.regDate).getTime()
        },
        {
            title: "Category",
            dataIndex: "category",
            key: "category",
            className: "text-center",
            render: (val: any) => <span className="badge bg-primary-light text-primary">{val}</span>
        },
        {
            title: "Willingness",
            key: "willingness",
            className: "text-center",
            render: (_: any, record: PriorityRecord) => (
                record.isWillingnessGenerated ? (
                    <div className="d-flex flex-column align-items-center justify-content-center">
                        <span className="text-success" title={`Willingness Ref: ${record.willingnessReferenceNumber || 'N/A'} (${record.willingnessStatus || 'PENDING'})`}>
                            <i className="ti ti-circle-check-filled fs-18"></i>
                        </span>
                        {record.willingnessReferenceNumber && (
                            <span className="text-muted mt-0.5 fw-medium" style={{ fontSize: '11px', letterSpacing: '0.2px' }}>
                                {record.willingnessReferenceNumber}
                            </span>
                        )}
                    </div>
                ) : (
                    <span className="text-muted">-</span>
                )
            )
        },
        {
            title: "Action",
            key: "action",
            className: "text-center",
            render: (_: any, record: any) => (
                <div className="d-flex align-items-center justify-content-center gap-2">
                    <Link
                        to={routes.staffQuarterAdmin.requestDetails.replace(":id", encodeRouteParam(record.key))}
                        className="btn btn-sm btn-icon btn-light border"
                        title="View Details"
                    >
                        <i className="ti ti-eye text-primary"></i>
                    </Link>
                </div>
            )
        },
    ];

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                {/* Page Header */}
                <div className="page-header mb-4">
                    <div className="row align-items-center">
                        <div className="col">
                            <h3 className="page-title text-dark fw-bold">Priority List Management</h3>
                            <ul className="breadcrumb bg-transparent p-0 mt-1">
                                <li className="breadcrumb-item">
                                    <Link to={routes.staffQuarterAdmin.dashboard}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item active">Priority List</li>
                            </ul>
                        </div>
                        <div className="col-auto float-end ms-auto">
                        </div>
                    </div>
                </div>

                {/* FILTERS */}
                <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '8px', background: '#ffffff' }}>
                    <div className="card-body p-2">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                            <div className="d-flex flex-wrap align-items-center gap-2">
                                <div className="input-group input-group-sm" style={{ width: '240px' }}>
                                    <span className="input-group-text bg-white border-end-0">
                                        <i className="ti ti-search text-muted" style={{ fontSize: '14px' }}></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm border-start-0 ps-0"
                                        placeholder="Search Emp No. or Name..."
                                        value={searchTerm}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setSearchTerm(val);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                fetchData(1, pagination.pageSize, selectedType, searchTerm, selectedLocation, selectedPoolId, selectedCategory);
                                            }
                                        }}
                                        style={{ fontSize: '12.5px', height: '32px' }}
                                    />
                                    {searchTerm && (
                                        <button
                                            className="btn btn-sm btn-outline-secondary border-start-0 bg-white"
                                            type="button"
                                            onClick={() => {
                                                setSearchTerm("");
                                                fetchData(1, pagination.pageSize, selectedType, "", selectedLocation, selectedPoolId, selectedCategory);
                                            }}
                                            style={{ height: '32px' }}
                                        >
                                            <i className="ti ti-x" style={{ fontSize: '12px' }}></i>
                                        </button>
                                    )}
                                </div>
                                <button
                                    className="btn btn-sm btn-primary shadow-sm d-inline-flex align-items-center justify-content-center"
                                    style={{ height: '32px', fontSize: '12px', borderRadius: '6px' }}
                                    onClick={() => {
                                        fetchData(1, pagination.pageSize, selectedType, searchTerm, selectedLocation, selectedPoolId, selectedCategory);
                                    }}
                                >
                                    <i className="ti ti-search me-1"></i> Search
                                </button>
                                <Select
                                    className="custom-select-ui-sm"
                                    style={{ width: '160px' }}
                                    showSearch
                                    optionFilterProp="children"
                                    value={selectedType || undefined}
                                    onChange={(value) => {
                                        setSelectedType(value || "");
                                        fetchData(1, pagination.pageSize, value || "", searchTerm, selectedLocation, selectedPoolId, selectedCategory);
                                    }}
                                    options={filterOptions.types.map((t: any) => ({ value: t.code, label: t.name }))}
                                    placeholder="Quarter Type"
                                    size="small"
                                />
                                <Select
                                    className="custom-select-ui-sm"
                                    style={{ width: '160px' }}
                                    showSearch
                                    optionFilterProp="children"
                                    value={selectedLocation || undefined}
                                    onChange={(value) => {
                                        setSelectedLocation(value || "");
                                        fetchData(1, pagination.pageSize, selectedType, searchTerm, value || "", selectedPoolId, selectedCategory);
                                    }}
                                    options={filterOptions.locations.map(l => ({ value: l.id?.toString(), label: l.name }))}
                                    placeholder="Location"
                                    size="small"
                                />
                                <Select
                                    className="custom-select-ui-sm"
                                    style={{ width: '160px' }}
                                    showSearch
                                    optionFilterProp="children"
                                    value={selectedPoolId || undefined}
                                    onChange={(value) => {
                                        setSelectedPoolId(value || "");
                                        fetchData(1, pagination.pageSize, selectedType, searchTerm, selectedLocation, value || "", selectedCategory);
                                    }}
                                    options={filterOptions.pools.map(p => ({ value: p.id?.toString(), label: p.namePool }))}
                                    placeholder="Pool Type"
                                    size="small"
                                />
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <button
                                    className="btn btn-sm btn-white border shadow-sm d-inline-flex align-items-center justify-content-center"
                                    style={{ height: '32px', fontSize: '12px', borderRadius: '6px' }}
                                    onClick={handleExport}
                                    disabled={isExporting}
                                >
                                    <i className={`ti ${isExporting ? 'ti-loader-2 spin' : 'ti-file-export'} me-1 text-muted`}></i>
                                    Export
                                </button>
                                <button
                                    className="btn btn-sm btn-danger shadow-sm d-inline-flex align-items-center justify-content-center"
                                    style={{ height: '32px', fontSize: '12px', borderRadius: '6px' }}
                                    onClick={() => {
                                        setSearchTerm("");
                                        setSelectedType("");
                                        setSelectedLocation("");
                                        setSelectedPoolId("");
                                        fetchData(1, pagination.pageSize, "", "", "", "");
                                    }}
                                >
                                    <i className="ti ti-refresh me-1"></i> Reset
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* TABLE */}
                <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                    <div className="card-body p-0 py-3 custom-table-layout">
                        <Table
                            columns={columns}
                            dataSource={data}
                            Selection={false}
                            paginationConfig={pagination}
                            handlePagination={handlePagination}
                            loading={isLoading}
                        />
                    </div>
                </div>

                <Modal
                    title="Generate Willingness"
                    open={isConfirmModalOpen}
                    onOk={handleConfirmWillingness}
                    onCancel={() => setIsConfirmModalOpen(false)}
                    confirmLoading={isGenerating}
                    okText="Generate"
                    cancelText="Cancel"
                    zIndex={9999}
                >
                    <div className="py-3 text-center">
                        <i className="ti ti-info-circle text-primary mb-3" style={{ fontSize: '48px' }}></i>
                        <p className="mb-0 fs-16 fw-medium">
                            Are you sure you want to generate willingness for <br />
                            <span className="text-primary fw-bold">{selectedRecord?.name}</span>
                            {selectedRecord?.registrationReferenceNumber && (
                                <span className="text-muted small ms-2" title="EMP ID">
                                    (EMP ID: {selectedRecord.empNo})
                                </span>
                            )}
                        </p>
                        <p className="text-muted small mt-2">
                            This will initiate the willingness confirmation process for the selected employee.
                        </p>
                    </div>
                </Modal>

            </div>
            <style>{`
          .bg-success-light { background-color: rgba(25, 135, 84, 0.1); }
          .bg-danger-light { background-color: rgba(220, 53, 69, 0.1); }
          .bg-info-light { background-color: rgba(13, 202, 240, 0.1); }
          .bg-primary-light { background-color: rgba(var(--bs-primary-rgb), 0.1); }
          .hover-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important;
          }
          .spin {
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .custom-table-layout {
              display: flex;
              flex-direction: column;
          }
          .custom-table-layout .table-top-data {
              order: 2;
              margin-top: 5px;
              padding: 0 15px;
          }
          .custom-table-layout .table.datanew {
              order: 1;
          }
          .custom-table-layout .dataTables_filter {
              display: none !important;
          }
          .custom-table-layout .dataTables_length {
              justify-content: flex-end;
          }
          .custom-table-layout .table-top-data .row {
              flex-direction: row-reverse;
          }
          .custom-select-ui .ant-select-selector {
              border-radius: 8px !important;
              border-color: #e2e8f0 !important;
              box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
          }
          .custom-select-ui .ant-select-selector:hover {
              border-color: #cbd5e1 !important;
          }
          .custom-select-ui-sm .ant-select-selector {
              border-radius: 6px !important;
              height: 32px !important;
              padding: 0 8px !important;
              display: flex;
              align-items: center;
              border-color: #e2e8f0 !important;
              font-size: 12.5px !important;
              box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
          }
          .custom-select-ui-sm .ant-select-selector:hover {
              border-color: #cbd5e1 !important;
          }
          .custom-select-ui-sm .ant-select-selection-item, 
          .custom-select-ui-sm .ant-select-selection-placeholder {
              line-height: 30px !important;
          }
      `}</style>
        </div>
    );
};

export default PriorityList;
